import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Unlock, X } from "lucide-react";
import { useSecrets } from "@/context/SecretsContext";
import { PUZZLE } from "@/data/secrets";

/**
 * The obsolete optical trunk: the one line on this site that still works
 * when everything else reports nominal.
 */
export const ManualTransmit = () => {
  const navigate = useNavigate();
  const {
    transmitOpen,
    setTransmitOpen,
    evaluateCode,
    unlock,
    unlocked,
    fragments
  } = useSecrets();
  const [code, setCode] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | near | rejected

  const submit = (e) => {
    e.preventDefault();
    const result = evaluateCode(code);
    if (result === "accepted") {
      setStatus("idle");
      unlock();
      toast.success("Authority accepted", {
        description: "Clearance granted. Opening the sixth file."
      });
      navigate("/clearance");
      return;
    }
    setStatus(result);
    setAttempts((a) => a + 1);
  };

  const hint =
    attempts >= 4
      ? PUZZLE.hintThree
      : attempts >= 2
        ? PUZZLE.hintTwo
        : fragments.length > 0
          ? PUZZLE.hintOne
          : "Three fragments are recoverable from this dossier. Probe what the systems refuse to show you.";

  return (
    <div id="manual-transmit" className="scroll-mt-24">
      {!transmitOpen ? (
        <button
          type="button"
          onClick={() => setTransmitOpen(true)}
          data-testid="manual-transmit-toggle"
          className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(215_8%_42%)] transition-colors duration-150 hover:text-[hsl(174_62%_60%)]"
        >
          <span className="h-1.5 w-1.5 rounded-full border border-[hsl(215_10%_40%)] transition-colors duration-150 group-hover:border-[hsl(174_72%_46%)] group-hover:bg-[hsl(174_72%_46%)]" />
          Manual transmit line · sealed
        </button>
      ) : (
        <div
          className="panel-quiet max-w-lg px-4 py-4"
          data-testid="manual-transmit-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(174_62%_60%)]">
              Optical trunk · obsolete · safety seal broken
            </p>
            <button
              type="button"
              onClick={() => setTransmitOpen(false)}
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              Close
            </button>
          </div>

          {unlocked ? (
            <div className="mt-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(174_62%_64%)]">
                Authority accepted · clearance already granted
              </p>
              <button
                type="button"
                onClick={() => navigate("/clearance")}
                data-testid="transmit-open-clearance"
                className="mt-3 inline-flex h-10 items-center gap-2 rounded-[10px] bg-primary px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
              >
                <Unlock className="h-3.5 w-3.5" />
                Open the sixth file
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4">
              <label
                htmlFor="transmit-code"
                className="block font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(210_14%_80%)]"
              >
                {PUZZLE.prompt}
              </label>
              <p className="mt-1.5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(215_10%_52%)]">
                {PUZZLE.subPrompt}
              </p>

              <div className="mt-2.5 flex flex-col gap-2 sm:flex-row">
                <input
                  id="transmit-code"
                  data-testid="transmit-code-input"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (status !== "idle") setStatus("idle");
                  }}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="02:--:--"
                  aria-invalid={status !== "idle"}
                  className="h-11 flex-1 rounded-[10px] border border-border bg-[hsl(220_18%_7%)] px-3.5 font-mono text-sm tabular-nums tracking-[0.12em] text-foreground placeholder:text-[hsl(215_10%_38%)] focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-[invalid=true]:border-[hsl(2_72%_46%/0.7)]"
                />
                <button
                  type="submit"
                  data-testid="transmit-submit-button"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-primary px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
                >
                  <Radio className="h-3.5 w-3.5" />
                  Transmit
                </button>
              </div>

              {status !== "idle" && (
                <p
                  data-testid="transmit-error"
                  role="alert"
                  className={`mt-3 font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] ${
                    status === "near"
                      ? "text-[hsl(32_88%_66%)]"
                      : "text-[hsl(2_80%_66%)]"
                  }`}
                >
                  {status === "near"
                    ? PUZZLE.nearMissMessage
                    : PUZZLE.rejectMessage}
                </p>
              )}

              <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(215_10%_48%)]">
                {hint}
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

/** Small tray that tracks recovered fragments during the hunt. */
export const FragmentTray = () => {
  const { orderedFragments, allFragmentsFound, openTransmit, unlocked } =
    useSecrets();
  const [hidden, setHidden] = useState(false);

  if (unlocked || hidden || orderedFragments.length === 0) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-[70] sm:right-auto sm:bottom-4 sm:left-4 sm:w-80"
      data-testid="fragment-tray"
    >
      <div className="panel overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-[hsl(220_18%_8%)] px-3.5 py-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(32_80%_66%)]">
            Recovered fragments
          </p>
          <span className="flex items-center gap-2">
            <p className="font-mono text-[10px] tabular-nums text-muted-foreground">
              {orderedFragments.length}/3
            </p>
            <button
              type="button"
              onClick={() => setHidden(true)}
              aria-label="Hide fragment tray"
              data-testid="fragment-tray-hide"
              className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-muted-foreground transition-colors duration-150 hover:bg-white/5 hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>

        <div className="px-3.5 py-3">
          <div className="flex items-center gap-2">
            {[0, 1, 2].map((i) => {
              const frag = orderedFragments[i];
              return (
                <span
                  key={i}
                  className={`flex h-10 flex-1 items-center justify-center rounded-[8px] border font-mono text-sm tabular-nums ${
                    frag
                      ? "border-[hsl(174_72%_42%/0.5)] bg-[hsl(174_40%_40%/0.1)] text-[hsl(174_62%_68%)]"
                      : "border-dashed border-[hsl(220_12%_24%)] text-[hsl(215_10%_34%)]"
                  }`}
                >
                  {frag ? frag.value : "--"}
                </span>
              );
            })}
          </div>

          {allFragmentsFound ? (
            <>
              <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(210_14%_76%)]">
                Assembled: 02 18 54 — the moment the record ends.
                <span className="mt-1 block text-[hsl(32_80%_66%)]">
                  Add the 6.13 seconds that are missing.
                </span>
              </p>
              <button
                type="button"
                onClick={openTransmit}
                data-testid="fragment-tray-transmit"
                className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-[10px] bg-primary px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
              >
                <Radio className="h-3.5 w-3.5" />
                Open the transmit line
              </button>
            </>
          ) : (
            <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(215_10%_50%)]">
              Keep probing. The systems that insist nothing is there are the
              ones worth touching.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
